import {AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal, WritableSignal} from '@angular/core';
import {ContentService} from "../../_services/content.service";
import {SnackbarService} from "../../_services/snackbar.service";
import {AuthService} from "../../_services/auth.service";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {Router} from "@angular/router";
import {AdminService} from "../../_services/admin.service";
import {UploadSessionFormComponent} from "../../admin/upload-session-form/upload-session-form.component";
import {ReducedUser, User} from "../../_types/user";
import {ModifiedPermission} from "../../admin/users/user/user.component";
import {TimelineOwner} from "../../_types/memorybox";
import {ConfirmDialogComponent} from "../confirm-dialog/confirm-dialog.component";
import {ConfirmationDialogData} from "../../_types/shared";

interface CoalatedPermissions {
    id: number,
    timeline: number,
    name?: string,
    newName?: string,
    loading?: boolean,
    created_date?: string,
    modified_date?: string,
    deletion_requested_date?: string,
    deleted?: boolean,
    timeline_id?: number,
    timeline_child?: TimelineOwner,
    permission: string
    timeline_name?: string;
    user?: ReducedUser | {
        first_name: string
        full_name: string
        id: number
        is_memory_box: boolean
        last_name: string
        photo?: string
        preferred_name: string
        role: { name: string, role_type: string, can_have_timeline: boolean }
        thumbnail?: string
        username?: string;
    };
    changeRequest?: string;
    newPermission?: string;
    is_owner?: boolean;
    is_direct?: boolean;
}

@Component({
    selector: 'app-manage-access-table',
    templateUrl: './manage-access-table.component.html',
    styleUrl: './manage-access-table.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ManageAccessTableComponent implements AfterViewInit {
    @Input() permissions: CoalatedPermissions[] = [];
    @Input() canRequest: boolean = false;
    @Input() hideAdmins: boolean = false;
    @Input() myMemoryBox: boolean = false;
    @Input() isProfile: boolean = false;
    @Input() currentAccess?: boolean = false;
    @Input() hideCurrentUser?: number | undefined = undefined;
    @Input() timelineName?: string = '';
    @Input() firstName?: string = '';
    @Input() timeline: number | undefined;
    @Input() child: number | undefined;
    @Input() showUploadSessionForm: boolean = false;
    @Input() showOwnerColumn: boolean = false;
    @Input() showRelationship: boolean = false;
    @Output() updatePermissions: EventEmitter<null> = new EventEmitter();
    currentUser!: User;
    displayedColumns: string[] = ['profile', 'name', 'role'];
    @Input() childProfile: ReducedUser | undefined;
    permissionOptions: { name: string, value: string }[] = [];
    isRaa: boolean = false;
    configurationError: WritableSignal<boolean> = signal(false);
    private configurationReady: WritableSignal<boolean> = signal(false);
    private childProfileLoaded: WritableSignal<boolean> = signal(false);

    get tableReady(): boolean {
        return this.configurationReady() && !!this.currentUser &&
            (this.myMemoryBox || !this.child || !!this.childProfile || this.childProfileLoaded());
    }

    constructor(private contentService: ContentService,
                private snackbarService: SnackbarService,
                public dialog: MatDialog,
                private adminService: AdminService,
                private authService: AuthService,
                private router: Router) {

        this.authService.getUser().subscribe({
            next: user => {
                this.currentUser = user;
                this.loadConfiguration();
            }
        });
    }

    loadConfiguration(): void {
        this.configurationReady.set(false);
        this.configurationError.set(false);
        Promise.all([
            this.authService.getDynamicSettings(),
            this.authService.getPermissions()
        ]).then(([settings, permissions]): void => {
            this.isRaa = settings.is_raa;
            const columns = ['profile', 'name', 'role'];
            if (this.isRaa) {
                if (this.showRelationship) {
                    columns.push('relationship', 'access')
                }
                if (this.currentUser?.role.name !== 'Birth Family') {
                    columns.push('permission');
                }
                if (this.currentUser?.role?.role_type === 'admin') {
                    columns.push('uploadSession');
                }
            } else {
                columns.push('permission');
            }
            this.displayedColumns = columns;
            this.permissionOptions = [...permissions, {value: 'No Permission', name: 'No Access'}];
            this.configurationReady.set(true);
        }).catch(error => {
            this.configurationError.set(true);
            this.snackbarService.openSnackBar(error, 'error');
            console.error(error);
        });
    }

    get visiblePermissions(): CoalatedPermissions[] {
        return this.permissions.filter((permission: CoalatedPermissions): boolean => {
            // Only set newName once to avoid overwriting user input in the ngModel
            if (permission.newName === undefined) {
                permission.newName = permission.name;
            }

            return this.shouldDisplayPermission(permission);
        });
    }


    ngAfterViewInit(): void {
        // if im checking the access of my memory box, then I need to check the roles on their permissions for raa limited
        // im checking the access of memory boxes if you have access to I need to check if I am raa limited
        if (!this.myMemoryBox && this.child && !this.childProfile) {
            this.contentService.getUserProfile(this.child).subscribe({
                next: r => {
                    this.childProfile = r;
                    this.childProfileLoaded.set(true);
                }, error: error => {
                    this.childProfileLoaded.set(true);
                    // this.snackbarService.openSnackBar(error, 'error')
                    console.error(error)
                }
            });
        }
    }

    editRelationshipAccess(timelineAccess: CoalatedPermissions): void {
        timelineAccess.loading = true;
        this.adminService.permissionUpdate(timelineAccess.id, {is_direct: timelineAccess.is_direct}).subscribe({
            next: r => {
                timelineAccess.loading = false;
                this.snackbarService.openSnackBar('Permission Updated', 'success')
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error');
                timelineAccess.loading = false;
                console.error(error);
            }
        });
    }

    editRelationshipName(timelineAccess: CoalatedPermissions): void {
        timelineAccess.loading = true;
        this.adminService.permissionUpdate(timelineAccess.id, {name: timelineAccess.newName}).subscribe({
            next: r => {
                timelineAccess.loading = false;
                this.snackbarService.openSnackBar('Permission Updated', 'success')
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error');
                timelineAccess.loading = false;
                console.error(error);
            }
        });
    }

    requestPermissionUpdate(timelineAccess: CoalatedPermissions): void {
        timelineAccess.loading = true;
        if (this.currentUser.role.role_type == 'admin' || this.currentUser?.role?.can_edit_permissions) {
            const timelineId: number = this.currentAccess ? timelineAccess.timeline : this.timeline || timelineAccess.timeline;
            let body = {
                user: this.currentAccess && this.child ? this.child : timelineAccess.user?.id,
                permission: timelineAccess.newPermission,
                timeline: timelineId
            }
            let timeline = timelineAccess.id;
            this.adminService.permissionUpdate(timeline, body).subscribe({
                next: r => {
                    if (timelineAccess.newPermission) {
                        timelineAccess.permission = timelineAccess.newPermission;
                    }
                    timelineAccess.changeRequest = undefined;
                    this.snackbarService.openSnackBar('Permission Updated', 'success')
                    this.updatePermissions.emit();
                    timelineAccess.loading = false;
                }, error: error => {
                    timelineAccess.loading = false;
                    this.snackbarService.openSnackBar(error, 'error')
                    console.error(error)
                }
            });

        } else if (this.currentUser?.role?.can_request_permission_changes && this.timeline) {
            let body = {
                user: timelineAccess.user?.id,
                permission: timelineAccess.newPermission,
                message: timelineAccess.changeRequest
            }

            this.contentService.requestPermissionUpdate(this.timeline, body).subscribe({
                next: r => {
                    timelineAccess.newPermission = timelineAccess.permission;
                    timelineAccess.changeRequest = undefined;
                    this.snackbarService.openSnackBar('Permission Request Sent', 'success')
                    timelineAccess.loading = false;
                }, error: error => {
                    this.snackbarService.openSnackBar(error, 'error')
                    timelineAccess.loading = false;
                    console.error(error)
                }
            });
        } else {
            this.snackbarService.openSnackBar('User role cannot request or change permissions', 'error')
            timelineAccess.loading = false;
        }
    }

    revokeAccess(timelineAccess: CoalatedPermissions): void {
        const timelineId: number = this.currentAccess ? timelineAccess.timeline : this.timeline || timelineAccess.timeline;
        let body = {
            user: this.currentAccess && this.child ? this.child : timelineAccess.user?.id,
            permission: 'No Permission',
            timeline: timelineId
        }
        let timeline: number = timelineAccess.id;

        this.adminService.permissionUpdate(timeline, body).subscribe({
            next: r => {
                timelineAccess.permission = 'No Permission';
                timelineAccess.changeRequest = undefined;
                this.snackbarService.openSnackBar('Permission Updated', 'success')
                this.updatePermissions.emit();
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });
    }

    isMemoryBoxType(permission: CoalatedPermissions): boolean {
        return (permission.user && 'is_memory_box' in permission.user && permission.user.is_memory_box) || false;
    }

    viewProfile(permission: CoalatedPermissions): void {
        if (!permission.user?.id) {
            return
        }

        const isMemoryBox: boolean = this.isMemoryBoxType(permission);
        if (this.currentUser.role.role_type == 'admin') {
            // navigating to the same page but with different ids doesnt trigger OnInit so have to reload
            if (!isMemoryBox) {
                this.router.navigate(['/user', permission.user?.id]).then(function (): void {
                    location.reload()
                });
            } else {
                this.router.navigate(['/timelines', permission.user?.id]).then(function (): void {
                    location.reload()
                });
            }
        } else if (this.currentUser.role.can_access_profiles) {
            if (!isMemoryBox) {
                this.router.navigate(['/profile', permission.user?.id]).then(function (): void {
                    location.reload()
                });
            } else {
                this.router.navigate(['/profile', 0, permission.user?.id]).then(function (): void {
                    location.reload()
                });
            }
            this.dialog.closeAll();
        }
    }

    openUploadSession(permission: ModifiedPermission): void {
        if (this.myMemoryBox && this.timelineName) {
            permission.timeline_name = this.timelineName
        }
        this.dialog.open(UploadSessionFormComponent, {
            panelClass: 'mediumWindow',
            data: {
                user: this.myMemoryBox ? permission?.user?.id : this.child,
                permission: permission
            }
        });
    }

    getEffectivePermission(permission: CoalatedPermissions): string {
        return permission.newPermission || permission.permission;
    }

    shouldShowUploadFrequency(permission: CoalatedPermissions): boolean {
        return this.getEffectivePermission(permission) !== 'View Only' && this.getEffectivePermission(permission) !== 'No Permission';
    }

    shouldDisplayPermission(permission: CoalatedPermissions): boolean {
        if (this.hideAdmins && permission.user?.role?.role_type == 'admin') {
            return false;
        }
        // hide current user
        if (this.hideCurrentUser && permission.timeline == this.hideCurrentUser) {
            return false;
        }
        if (!this.isRaa || !this.child) {
            return true;
        }

        return !(permission.user?.id == this.child && permission.user?.role?.role_type == 'young_person');
    }

    canChangePermission(user: User): boolean {
        if (!this.isRaa) {
            return true;
        }

        // Access to a box uses the row user's role; a user's boxes use their profile role.
        const role = this.myMemoryBox ? user?.role : this.childProfile?.role;
        return role?.is_fixed === false;
    }

    makeOwner(user: User): void {
        const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open<ConfirmDialogComponent, ConfirmationDialogData>(ConfirmDialogComponent, {
            panelClass: 'mediumWindow',
            data: {
                showSubmitBtn: true,
                showCancelBtn: true,
                message: 'Are you sure you want to make ' + (this.authService.getDisplayName(user)) + ' the owner of this Memory Box?',
                title: 'New Memory Box Owner',
                showHeader: true
            }
        });
        dialogRef.afterClosed().subscribe((resp): void => {
            if (resp && this.timeline) {
                let body = {new_owner_id: user.id, current_owner_id: this.child}
                this.adminService.setTimelineOwner(this.timeline, body).subscribe({
                    next: r => {
                        this.snackbarService.openSnackBar('Owner Updated', 'success')
                        this.child = user.id;
                    }, error: error => {
                        this.snackbarService.openSnackBar(error, 'error')
                        console.error(error)
                    }
                });
            }
        });
    }
}
